import { Tooltip } from 'antd';
import { flatten, isEqual, omit, sortBy, uniq } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { SchemaSelect } from 'components/damages/DamageSchema/SchemaSelect';
import { useDamageAttrs } from 'components/damages/withDamages';
import {
  confirmationStatusColumn,
  CREATED_DATE_RANGE_LIMIT,
  criticalColumn,
  damageIdColumn,
  getAssetColumn,
  getAssetStatusColumn,
  getFuturePropagationColumn,
  getInspectionsColumn,
  getObservationsColumn,
  getPrimaryGroupSourceObservationCreatedColumn,
  getPropagationsColumn,
  getSiteColumn,
  tasksColumn,
} from 'components/DamageTable2/columns';
import { ConditionalDamageSchemaFilterDropdown } from 'components/DamageTable2/ConditionalDamageSchemaFilterDropdown';
import { DamageSchemaFilterSuggestionsPrefetch } from 'components/DamageTable2/DamageSchemaFilterSuggestionsPrefetch';
import {
  type PresetFilterType,
  DataTableContext,
  DataTableContextProvider,
  DataTableWithoutContext,
} from 'components/DataTable';
import {
  consumeSiteFilterChange,
  DefaultStateToGraphqlKeyMap,
  LockedFilterDropdown,
  omitMakeModelFiltersForSiteChange,
} from 'components/DataTable/customFilters';
import { getMultiSortBy, getSortBy } from 'components/DataTable/paging';
import {
  type TDataTableStorageKey,
  type TQueryFilter,
  EFilterSortClearType,
} from 'components/DataTable/types';
import { useTableScrollHeight } from 'components/DataTable/useTableScrollHeight';
import {
  descSortConfig,
  mapColumnsWithWidth,
  resolveFilterBy,
  tableStorage,
} from 'components/DataTable/utils';
import { columnPickerStorage } from 'components/DataTable/Views/ColumnPicker/utils';
import { HORIZON_DAMAGE_TABLE_STORAGE_KEY } from 'components/DataTable/Views/helpers/tableStorageKeys';
import { multiSorterStorage } from 'components/DataTable/Views/MultiSorter/utils';
import { ExportReport } from 'components/ExportReport';
import { type GetConditionalReportArgs } from 'components/ExportReport/types';
import { getTimeZoneReportArg, isExcelCustomViewTemplate } from 'components/ExportReport/utils';
import {
  BladeDamagePlotContainer,
  getMakeModelListLabel,
  MAX_PLOT_BLADE_LENGTH_SPREAD_M,
  usePlotBladeSet,
} from 'horizon/components/Damages/BladeDamagePlot';
import { DamagesBulkEdit } from 'horizon/components/Damages/DamagesBulkEdit';
import { DamageSearch } from 'horizon/components/Damages/DamageSearch';
import {
  DamagesTablePreferenceError,
  shouldDeferDamages2Query,
  useDamagesTablePreLoading,
} from 'horizon/components/Damages/damagesTablePreLoading';
import { useDefectPatternsEnabled } from 'horizon/components/Damages/DefectPatterns/useDefectPatternsEnabled';
import {
  getRepresentativeBlade,
  isDefectPatternViewType,
} from 'horizon/components/Damages/DefectPatterns/useDefectPatternViews';
import {
  type MergeConfirmationState,
  MergeDamagesModal,
} from 'horizon/components/Damages/MergeDamagesModal';
import { MultiInspectionDamageIngestion } from 'horizon/components/Damages/MultiInspectionDamageIngestion/MultiInspectionDamageIngestion';
import {
  getUnconfirmedDamages,
  useBulkConfirmDamages,
} from 'horizon/routes/DamagesInbox/DamageReview/useBulkConfirmDamages';
import {
  CREATE_TASKS_DAMAGE_LIMITS,
  CreateTasksFromDamagesModal,
} from 'horizon/routes/Inspections/DamageList/CreateTasksFromDamagesModal';
import { generateDamageTableFilters } from 'horizon/routes/Inspections/DamageList/utils';
import { type CustomFilterDropdownProps, type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAssetType,
  type AtlasGqlGetHorizonDamagesQuery,
  type AtlasGqlHorizonDamage,
  type AtlasGqlTableView,
  AtlasGqlFilterOperator,
  AtlasGqlReportContext,
  GetHorizonDamagesDocument,
  useAvailableComponentStatusesQuery,
} from 'types/atlas-graphql';
import { DAMAGE_CONFIRM, DAMAGE_EDIT_ATTRIBUTES, TASK_CREATE } from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { isProd, isProdReview } from 'utils/env';
import { useFeatures } from 'utils/features';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';
import { SelectableAction, useSelectableRecords } from 'utils/selectable';
import {
  getItem as getLocalStorageItem,
  removeItem as removeLocalStorageItem,
  setItem as setLocalStorageItem,
} from 'utils/storage';
import { RenderWithAccess, usePermissions } from 'utils/usePermissions';

import { FilterControlsContainer } from './DamageTable2.style';
import { DamageList2ExpandedRow } from './ExpandedRow';
import { type TDataQueryChangeArgs } from './types';
import {
  getDamages2CustomViewSortByArg,
  getDamages2CustomViewVisibleColumnsArg,
  getInitialPresetFilter,
  getLockedFilters,
  hasScopingUniversalFilters,
  shouldIncludeDamageSchemaFilterSearch,
} from './utils';

const DAMAGE_COLUMNS_FOR_SORT_CHANGE = ['Date', 'Severity', 'Distance', 'Length', 'Width', 'Chord'];

type DamageTable2Props = {
  id: string;
  title: string;
  defaultActiveTabKey?: string;
  damageIds?: string[];
  siteIds?: string[];
  assetIds?: string[];
  assetTypes?: AtlasGqlAssetType[];
  inspectionIds?: string[];
  // Filters the user can see but not change. A Damage schema column whose key has an EQUALS
  // entry is locked. Scope Sites and Assets with `siteIds` / `assetIds`.
  universalFilter?: TQueryFilter[];
  // Used only when no table state is saved. Keyed like `filteredInfo`.
  initialFilters?: Record<string, unknown>;
  critical?: boolean;
  controls?: boolean;
  savedViews?: boolean;
  externalLoading?: boolean;
  plotOpenByDefault?: boolean;
  storageKey?: TDataTableStorageKey;
  // actions menu items a page leaves out because they reach beyond what it shows
  hiddenActions?: ('idSearch' | 'uploadDamageData')[];
  // for counts kept outside the table
  mutationRefetchQueries?: string[];
};

type BaseDamageTable2Props = DamageTable2Props & {
  initialPresetFilter?: PresetFilterType;
};

const BaseDamageTable2: React.FunctionComponent<BaseDamageTable2Props> = ({
  id,
  title,
  damageIds,
  siteIds,
  assetIds,
  assetTypes,
  inspectionIds,
  universalFilter,
  critical = false,
  controls = true,
  savedViews = true,
  externalLoading = false,
  plotOpenByDefault = true,
  storageKey = HORIZON_DAMAGE_TABLE_STORAGE_KEY,
  hiddenActions = [],
  mutationRefetchQueries,
  initialPresetFilter,
}) => {
  const { t } = useTranslation();
  const [filterBy, setFilterBy] = useState<TQueryFilter[]>([]);
  const [totalCount, setTotalCount] = useState<number>();
  const rowKeys = useRef<string[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  const [selectedSchema, setSelectedSchema] = useState<string>();
  const [createTasksFromDamagesModalOpen, setCreateTasksFromDamagesModalOpen] = useState(false);
  const [damageSearchOpen, setDamageSearchOpen] = useState<boolean>(false);
  const [multiInspectionDamageIngestionModalOpen, setMultiInspectionDamageIngestionModalOpen] =
    useState<boolean>(false);
  const [mergeConfirmationState, setMergeConfirmationState] = useState<MergeConfirmationState>({
    isOpen: false,
  });
  const [mergedDamageId, setMergedDamageId] = useState<string>();
  const [serverDataTableLoading, setServerDataTableLoading] = useState<boolean>(false);
  // TODO: may want to put this as yet another thing in local storage, but leaving it here for now for development
  const [showPlot, setShowPlot] = useState<boolean>(false);
  const [bannerMessage, setBannerMessage] = useState<string>();
  const dataTableRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const { hasRole, canAccess } = usePermissions();
  const isEditor = hasRole('role-editor');
  const { confirm, confirming } = useBulkConfirmDamages(mutationRefetchQueries);

  const {
    DAMAGE_PLOT: hasDamagePlotFeatureFlag,
    DAMAGE_INGESTION: hasDamageIngestionFeatureFlag,
    RISK_MANAGEMENT: hasRiskManagementFeatureFlag,
  } = useFeatures().features;
  const hasDefectPatterns = useDefectPatternsEnabled();

  const { customer, user, loading: accountLoading } = useAccountContext();
  const {
    isTablePreferenceLoading,
    isTablePreferenceUnavailable,
    refetchTablePreference,
    shouldDeferUntilFiltered,
  } = useDamagesTablePreLoading();

  const isSiteDetailsPage = siteIds?.length === 1 && !assetIds?.length;
  const isTurbineDetailsPage =
    assetIds?.length === 1 && assetTypes?.length === 1 && assetTypes?.[0]?.name === 'Turbine';
  const isBladeDetailsPage =
    assetIds?.length === 1 && assetTypes?.length === 1 && assetTypes?.[0]?.name === 'Blade';

  const tableViewStorageManager = tableStorage({ storageKey: `${id}_VIEW`, user });
  const columnPickerStorageManager = columnPickerStorage({
    storageKey: storageKey.COLUMN_PICKER,
  });
  const multiSorterStorageManager = multiSorterStorage({
    storageKey: storageKey.MULTI_SORT,
  });

  const {
    pagination,
    filteredInfo,
    updateFilteredInfo,
    sortedInfo,
    updateSortedInfo,
    setPersistedState,
    tableView,
    updateTableView,
    customizedColumnSet,
    updateCustomizedColumnSet,
    multiSortDef,
    updateMultiSortDef,
  } = useContext(DataTableContext);

  const previousSiteFilterRef = useRef<string[] | null | undefined>(null);
  useEffect(() => {
    const { site, asset } = filteredInfo ?? {};

    let updatedSiteFilter: string[] | null | undefined = site;
    let updatedAssetFilter:
      { key: string; value: (number | number[] | string)[] | string }[] | null = asset;

    // On a page scoped to Sites, ensure those Sites are in filters (prevents issues when switching between pages)
    if (siteIds?.length && !isEqual(filteredInfo?.site, siteIds)) {
      updatedSiteFilter = siteIds;
    }

    // Detect change on the effective Site (after page default). Avoids clearing
    // Make/Model when Site Damages seeds `site` that was omitted from persistence.
    const siteChanged = consumeSiteFilterChange(previousSiteFilterRef, updatedSiteFilter);

    // If a previously filtered site is removed (including when the user changes between site details pages), remove any assets/components on that site
    if (asset?.some(({ key }) => ['assetId', 'componentId'].includes(key))) {
      const assetIds = asset
        ?.find(({ key }) => key === 'assetId')
        ?.value?.filter((v: string) => updatedSiteFilter?.includes(v.split(':')[1]));
      const componentIds = asset
        ?.find(({ key }) => key === 'componentId')
        ?.value?.filter((v: string) => updatedSiteFilter?.includes(v.split(':')[1]));

      updatedAssetFilter = [
        ...asset?.filter(({ key }) => !['assetId', 'componentId'].includes(key)),
        assetIds?.length ? { key: 'assetId', value: assetIds } : undefined,
        componentIds?.length ? { key: 'componentId', value: componentIds } : undefined,
      ].filter(Boolean);
    }

    // Site details page navigation does not go through antd `extra.action === 'filter'`.
    // Fleet Site-filter OK is handled in `clearStaleAssetHierarchyFilters`.
    if (isSiteDetailsPage && siteChanged) {
      updatedAssetFilter =
        omitMakeModelFiltersForSiteChange(updatedAssetFilter, DefaultStateToGraphqlKeyMap) ?? null;
    }

    if (!isEqual(site, updatedSiteFilter) || !isEqual(asset, updatedAssetFilter)) {
      updateFilteredInfo({
        ...filteredInfo,
        site: updatedSiteFilter,
        asset: updatedAssetFilter,
      });
    }
  }, [isSiteDetailsPage, siteIds, filteredInfo?.site]); // Intentionally omitting filteredInfo/updateFilteredInfo so this happens only once per time site changes

  useEffect(() => {
    const localStorageKey = 'observationCreatedAutoFilterApplied';
    const observationCreatedColumnKey = 'primaryGroupSourceObservationCreated';
    const currentObservationCreatedFilter = filteredInfo?.[observationCreatedColumnKey];
    const hasExistingAutoFilter = getLocalStorageItem(localStorageKey) === 'true';

    if (sortedInfo?.columnKey === observationCreatedColumnKey) {
      const hasShortWindowFilter = currentObservationCreatedFilter?.some((filter: string[]) => {
        if (!Array.isArray(filter) || filter.length < 2) {
          return false;
        }
        const start = moment(filter[0]);
        const end = moment(filter[1]);
        return end.diff(start, 'days') <= CREATED_DATE_RANGE_LIMIT;
      });

      if (!hasShortWindowFilter) {
        const lastNDaysFilter = [
          moment().subtract(CREATED_DATE_RANGE_LIMIT, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
        ];
        updateFilteredInfo({
          ...filteredInfo,
          [observationCreatedColumnKey]: [lastNDaysFilter],
        });
        setLocalStorageItem(localStorageKey, 'true');
      }

      /**
       * Inform the user of the auto-filter, whether applied from a manual sort here (!hasShortWindowFilter)
       * or from table state in local storage or a saved view.
       */
      if (!hasShortWindowFilter || hasExistingAutoFilter) {
        setBannerMessage(
          t(
            'table.createdDateSortAutoFilterBanner',
            'To support sorting by Created Date, a filter for the past {{count}} days has been automatically applied. This range may be adjusted, but must be a window of at most {{count}} days.',
            { count: CREATED_DATE_RANGE_LIMIT }
          )
        );
      }
    } else if (currentObservationCreatedFilter && hasExistingAutoFilter) {
      updateFilteredInfo({ ...filteredInfo, [observationCreatedColumnKey]: null });
      removeLocalStorageItem(localStorageKey);
    }

    if (sortedInfo?.columnKey !== observationCreatedColumnKey) {
      setBannerMessage(undefined);
    }
  }, [sortedInfo, filteredInfo?.primaryGroupSourceObservationCreated, t]);

  const LINK_DAMAGES = { min: 2, max: 20 };
  const {
    selected = [],
    rowSelection,
    onClearSelection,
  } = useSelectableRecords<AtlasGqlHorizonDamage>();

  const { damageColumns, loading: loadingAttrs } = useDamageAttrs(selectedSchema, true, critical);

  const { data: componentStatusData } = useAvailableComponentStatusesQuery();

  /**
   * Defect patterns are make-agnostic, and one matched on Turbine model carries no Blade
   * make/model at all, so the view names a representative Blade. It scales the plot only —
   * the applied filters are untouched, so the Damages shown are still the pattern's own.
   */
  const representativeBlade = useMemo(
    () =>
      isDefectPatternViewType(tableView?.type) ? getRepresentativeBlade(tableView) : undefined,
    [tableView]
  );

  // Plot setup logic
  const plotBladeSet = usePlotBladeSet({
    skip: !hasDamagePlotFeatureFlag,
    siteIds,
    assetIds,
    representativeBlade,
    isSiteDetailsPage,
    isTurbineDetailsPage,
    isBladeDetailsPage,
  });
  const { canPlot, hasPlotFilters, makeModelsMissingLength } = plotBladeSet;
  const plotEnabled = hasDamagePlotFeatureFlag && canPlot;
  const makeModelsMissingLengthLabel =
    getMakeModelListLabel(makeModelsMissingLength) || t('common.unknown', 'Unknown');

  useEffect(() => {
    if (showPlot && !plotEnabled) {
      setShowPlot(false);
    }
  }, [showPlot, plotEnabled]);

  useEffect(() => {
    /**
     * Open plot by default if on a blade details page with valid make/model
     * or site or turbine details page where all blade descendants are of a
     * matching valid make/model
     */
    if (
      plotEnabled &&
      ((plotOpenByDefault && (isSiteDetailsPage || isTurbineDetailsPage || isBladeDetailsPage)) ||
        isDefectPatternViewType(tableView?.type))
    ) {
      setShowPlot(true);
    }
  }, [
    plotEnabled,
    plotOpenByDefault,
    isSiteDetailsPage,
    isTurbineDetailsPage,
    isBladeDetailsPage,
    tableView,
  ]);

  // End plot setup

  const selectedDamagesHaveSameAsset = useMemo(() => {
    return selected.every(
      (damage) => !!damage.asset?.id && damage.asset.id === selected[0].asset?.id
    );
  }, [selected]);

  const lockedFilters = useMemo(() => getLockedFilters(universalFilter), [universalFilter]);

  const defectPatternsScope = useMemo(
    () => (hasDefectPatterns ? { siteIds, assetIds } : undefined),
    [hasDefectPatterns, siteIds, assetIds]
  );

  const conditionalDamageColumns = useMemo(
    () =>
      damageColumns.map((column) => {
        if (typeof column.key !== 'string') {
          return column;
        }

        // Only the column's own key locks it, so Severity stays usable when `critical` is pinned.
        // A stale filter left on a locked column (saved state or a view) must not narrow the
        // pinned values, whatever its operator.
        const lockedValues = lockedFilters.get(column.key);
        if (lockedValues) {
          return {
            ...column,
            filtered: true,
            excludeFromServerFilter: true,
            filterDropdown: () => (
              <LockedFilterDropdown
                options={lockedValues.map((value) => ({ value, label: value }))}
              />
            ),
          };
        }

        if (column.type !== 'string') {
          return column;
        }

        return {
          ...column,
          filterDropdown: (props: CustomFilterDropdownProps) => (
            <ConditionalDamageSchemaFilterDropdown
              {...props}
              column={column}
              schemaColumns={damageColumns}
              schemaId={selectedSchema}
              includeSearch={shouldIncludeDamageSchemaFilterSearch(column.key, damageColumns)}
            />
          ),
        };
      }),
    [damageColumns, selectedSchema, lockedFilters]
  );

  const allColumns: TTableColumnDef[] = useMemo(
    () =>
      mapColumnsWithWidth([
        ...[
          damageIdColumn,
          confirmationStatusColumn,
          { ...tasksColumn, ...descSortConfig },
          getSiteColumn(siteIds),
          getAssetColumn(id, assetTypes, assetIds),
          getAssetStatusColumn(componentStatusData?.assetFieldDefinition?.fieldOptions ?? []),
          { ...getInspectionsColumn(), ...descSortConfig },
          { ...getObservationsColumn(), ...descSortConfig },
          getPropagationsColumn(),
          hasRiskManagementFeatureFlag && { ...getFuturePropagationColumn(), ...descSortConfig },
        ].filter((c): c is TTableColumnDef => !!c),
        ...conditionalDamageColumns.map((col) =>
          DAMAGE_COLUMNS_FOR_SORT_CHANGE.includes(col.key) ? { ...col, ...descSortConfig } : col
        ),
        {
          ...getPrimaryGroupSourceObservationCreatedColumn(
            sortedInfo?.columnKey === 'primaryGroupSourceObservationCreated'
          ),
          ...descSortConfig,
        },
      ]),
    [
      conditionalDamageColumns,
      componentStatusData,
      siteIds,
      sortedInfo,
      hasRiskManagementFeatureFlag,
    ]
  );

  // NOTE: these are necessary to synchronize the report export params with what is going on in the damage table
  const handleDataQueryChange = useCallback((args: TDataQueryChangeArgs) => {
    const { filterBy = [], totalCount = 0 } = args;
    setFilterBy((oldFilterBy) => {
      if (isEqual(oldFilterBy, filterBy)) {
        return oldFilterBy;
      }
      setAllExpanded(false);
      setExpandedRowKeys([]);

      return filterBy;
    });
    setTotalCount(totalCount);
  }, []);

  const universalFilters: TQueryFilter[] = useMemo(
    () => [
      ...generateDamageTableFilters({
        schemaId: selectedSchema,
        assetIds,
        siteIds,
        assetTypes,
        inspectionIds,
        damageIds,
        critical,
      }),
      ...(universalFilter ?? []),
    ],
    [
      selectedSchema,
      assetIds,
      assetTypes,
      siteIds,
      inspectionIds,
      damageIds,
      critical,
      universalFilter,
    ]
  );

  // The plot derives Site, Asset, schema and critical from the page, so it gets only the rest.
  const plotUniversalFilters: TQueryFilter[] = useMemo(
    () => [...generateDamageTableFilters({ inspectionIds }), ...(universalFilter ?? [])],
    [inspectionIds, universalFilter]
  );

  // What the page pins, for Conditions. Widest first, as the Inbox Filters menu lists them.
  // EQUALS, so the Site seeded into the Site column filter folds into this one.
  const pageLockedFilters: TQueryFilter[] = useMemo(
    () => [
      ...[
        ...generateDamageTableFilters({ siteIds }),
        ...generateDamageTableFilters({ assetIds, inspectionIds, critical }),
      ].map((filter) => ({ ...filter, operator: AtlasGqlFilterOperator.Equals })),
      ...(universalFilter ?? []),
    ],
    [siteIds, assetIds, inspectionIds, critical, universalFilter]
  );

  const transformQueryResponse = useCallback((data: AtlasGqlGetHorizonDamagesQuery) => {
    if (!data.getHorizonDamages) {
      return { totalCount: 0, items: [] };
    }

    const totalCount = data.getHorizonDamages?.totalCount ?? 0;
    const items = (data.getHorizonDamages?.items ?? []).filter(Boolean) as AtlasGqlHorizonDamage[];

    rowKeys.current = items.map(({ id }: AtlasGqlHorizonDamage) => id);

    return {
      totalCount,
      items: items.map((damage) => ({
        ...damage,
        attrs: damage.primaryObservationGroupAttrs,
      })),
    };
  }, []);

  const handleSelectionChange = (damages: AtlasGqlHorizonDamage[]) =>
    rowSelection.onChange(
      damages.map(({ id }) => id),
      damages
    );

  // The selection can change while a confirm is in flight, so its result applies to the
  // selection as it is when the confirm lands.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const handleConfirmSelected = async () => {
    const { confirmedIds } = await confirm(selected);
    // Keep only what failed, so a retry resends just those. The selected rows keep their
    // status from before the confirm, so a confirmed one left selected would go again.
    const confirmed = new Set(confirmedIds);
    handleSelectionChange(
      getUnconfirmedDamages(selectedRef.current).filter(({ id }) => !confirmed.has(id))
    );
  };

  const createTasksForSelectedDamages = (selected: AtlasGqlHorizonDamage[]) => {
    if (selected.length) {
      setCreateTasksFromDamagesModalOpen(true);
    }
  };

  // logic for setting the state of the expand/collapse all button
  useEffect(() => {
    if (expandedRowKeys.length === 0) {
      setAllExpanded(false);
    } else if (rowKeys.current.length === expandedRowKeys.length) {
      setAllExpanded(true);
    }
  }, [rowKeys, expandedRowKeys]);

  // resets the table when the schema is changed
  const handleChangeSchema = (schema: string) => {
    if (!!selectedSchema && schema !== selectedSchema) {
      // reset customized columns
      updateCustomizedColumnSet(undefined);
      columnPickerStorageManager.remove();
      // reset multisort
      updateMultiSortDef(undefined);
      multiSorterStorageManager.remove();
      // reset table view
      updateTableView(undefined);
      tableViewStorageManager.remove();

      // reset filters/sorters to their default values
      const { filters = {}, sortOrder = {} } = initialPresetFilter?.value ?? {};
      updateFilteredInfo(filters);
      updateSortedInfo(sortOrder);
      setPersistedState({ filteredInfo: filters, sortedInfo: sortOrder, pagination });
    }
    setSelectedSchema(schema);
  };

  // handle updates to the saved view
  const handleSavedViewChange = (tableView: AtlasGqlTableView) => {
    const { schemaId } = tableView.customFields ?? {};

    schemaId && setSelectedSchema(schemaId);
  };

  // handler for expanding/collapsing rows
  const handleToggleExpandAll = () => {
    if (allExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(rowKeys.current);
    }
  };

  const handleMergeSelected = useCallback(() => {
    const allObservations = flatten(
      flatten(
        selected.map((damage) => (damage.observationGroups ?? []).map((g) => g?.observations ?? []))
      )
    );

    // Find which damage has the oldest inspection and merge into that one
    const oldestObservation = sortBy(allObservations, ['inspection.inspectionDate'])[0];
    if (!!oldestObservation) {
      const targetDamageId: string = oldestObservation.damage?.id;
      setMergeConfirmationState({
        isOpen: true,
        targetDamageId,
        sourceDamageIds: uniq(
          allObservations
            .filter((o) => o?.damage?.id && o.damage.id !== targetDamageId)
            .map((o) => o?.damage?.id)
        ),
      });
    }
  }, [selected]);

  // arguments for damage report
  const damageReportArgs = [
    {
      key: 'filterBy',
      value: JSON.stringify([
        ...resolveFilterBy(universalFilters, filterBy),
        ...(selected?.length
          ? [
              {
                key: 'id',
                operator: 'EQUALS',
                values: selected.map(({ id }) => id),
              },
            ]
          : []),
      ]),
    },
  ];

  const handleGetConditionalReportArgs: GetConditionalReportArgs = useCallback(
    (template) => {
      if (!isExcelCustomViewTemplate(template?.id)) {
        return undefined;
      }

      return [
        getDamages2CustomViewVisibleColumnsArg({
          columnDefs: allColumns,
          customizedColumnSet,
        }),
        getDamages2CustomViewSortByArg({
          sortBy: getSortBy(sortedInfo),
          multiSortBy: getMultiSortBy(multiSortDef),
        }),
        getTimeZoneReportArg(),
      ];
    },
    [allColumns, customizedColumnSet, sortedInfo, multiSortDef]
  );

  // Ask DataTable to defer the unfiltered query. SkySpecs keeps the hardcoded
  // prod behavior; other orgs follow Disable Damages 2.0 Pre-Loading. Universal
  // scope filters (site/turbine pages) still short-circuit here.
  const deferQueryUntilFiltered = useMemo(() => {
    if (hasScopingUniversalFilters(universalFilters)) {
      return false;
    }

    return shouldDeferDamages2Query({
      customerId: customer?.id,
      isProdLikeEnv: isProd || isProdReview,
      shouldDeferUntilFiltered,
    });
  }, [customer?.id, shouldDeferUntilFiltered, universalFilters]);

  // Skip until account (and the shared table-preference query) have settled, and
  // until a schema is selected when filters are shown. A failed preference load
  // does not skip the query: the hook fail-safes to defer the unfiltered list,
  // while scoped/filtered pages still load and Retry refetches the preference.
  const skipQuery = accountLoading || isTablePreferenceLoading || (controls && !selectedSchema);
  const loading = externalLoading || loadingAttrs || skipQuery;
  const scrollHeight = useTableScrollHeight(dataTableRef, [
    loading,
    serverDataTableLoading,
    totalCount,
  ]);

  return (
    <>
      {isTablePreferenceUnavailable ? (
        <DamagesTablePreferenceError onRetry={() => void refetchTablePreference()} />
      ) : null}
      <DamageSchemaFilterSuggestionsPrefetch
        filteredInfo={filteredInfo}
        schemaColumns={damageColumns}
        schemaId={selectedSchema}
      />
      {hasDamagePlotFeatureFlag && (
        <BladeDamagePlotContainer
          isOpen={showPlot}
          plotEnabled={plotEnabled}
          onToggle={() => {
            setShowPlot((showPlot) => !showPlot);
          }}
          columns={allColumns}
          schemaId={selectedSchema}
          plotBladeSet={plotBladeSet}
          bladeAssetId={isBladeDetailsPage ? assetIds?.[0] : undefined}
          turbineId={isTurbineDetailsPage ? assetIds?.[0] : undefined}
          isSiteDetailsPage={isSiteDetailsPage}
          isTurbineDetailsPage={isTurbineDetailsPage}
          isBladeDetailsPage={isBladeDetailsPage}
          openByDefault={plotOpenByDefault}
          criticalOnly={critical === true}
          universalFilter={plotUniversalFilters}
          bladeSides={
            isDefectPatternViewType(tableView?.type)
              ? tableView?.filters?.['Blade Side']
              : undefined
          }
        />
      )}
      <div ref={dataTableRef}>
        <DataTableWithoutContext
          id={id}
          lockedFilters={pageLockedFilters}
          title={title}
          loading={loading}
          dataQuery={GetHorizonDamagesDocument}
          onDataQueryChange={handleDataQueryChange}
          transform={transformQueryResponse}
          setTotalCount={setTotalCount}
          initialPresetFilter={initialPresetFilter}
          clearSortAndFilterType={EFilterSortClearType.RESET}
          routeProps={{
            universalFilter: universalFilters,
            variables: { input: {}, includePredictedPropagation: !!hasRiskManagementFeatureFlag },
            skip: skipQuery,
            notifyOnNetworkStatusChange: true,
          }}
          deferCount={true}
          deferQueryUntilFiltered={deferQueryUntilFiltered}
          columns={allColumns}
          bannerMessage={bannerMessage}
          isExpandAllVisible={true}
          isExpandAllEnabled={true}
          allExpanded={allExpanded}
          handleExpandAll={handleToggleExpandAll}
          expandable={{
            expandedRowRender: (damage: AtlasGqlHorizonDamage) => (
              <DamageList2ExpandedRow
                damage={damage}
                selectedSchema={selectedSchema}
                lazyLoad={true}
                mergedDamageId={mergedDamageId}
                setMergedDamageId={setMergedDamageId}
                refetchQueries={mutationRefetchQueries}
              />
            ),
            expandedRowKeys,
            onExpandedRowsChange: (expandedKeys: string[]) => {
              setExpandedRowKeys(expandedKeys);
            },
          }}
          showColumnPicker={true}
          defectPatternsScope={defectPatternsScope}
          showSavedViews={savedViews}
          savedViewCustomFields={{ schemaId: selectedSchema }}
          onSavedViewChange={handleSavedViewChange}
          showMultiSorter={true}
          setLoading={setServerDataTableLoading}
          storageKey={storageKey}
          controls={
            controls ? (
              <FilterControlsContainer>
                <SchemaSelect
                  style={{ width: '200px' }}
                  selected={selectedSchema}
                  setSelected={handleChangeSchema}
                  storageKey={`${id}-schema-select`}
                />
              </FilterControlsContainer>
            ) : undefined
          }
          rowSelection={rowSelection}
          onClearSelection={onClearSelection}
          actions={
            <>
              <FlexContainer>
                <SelectableAction selected={selected} onClear={onClearSelection} />
                {!critical && (
                  <ExportReport
                    reportContexts={[
                      {
                        context: AtlasGqlReportContext.HorizonDamage,
                        reportArguments: [
                          {
                            args: damageReportArgs,
                            itemCount: selected?.length || totalCount,
                          },
                        ],
                      },
                    ]}
                    disabled={false}
                    cacheKey="damage-list-2"
                    getConditionalReportArgs={handleGetConditionalReportArgs}
                  />
                )}
              </FlexContainer>
              {!!selected.length && (
                <SpacedContainer>
                  <RenderWithAccess rules={[DAMAGE_EDIT_ATTRIBUTES]}>
                    <DamagesBulkEdit
                      selected={selected}
                      columns={[
                        isEditor ? criticalColumn : undefined,
                        ...damageColumns,
                        confirmationStatusColumn,
                      ].filter(Boolean)}
                      onFinish={onClearSelection}
                      refetchQueries={mutationRefetchQueries}
                    />
                  </RenderWithAccess>
                </SpacedContainer>
              )}
            </>
          }
          additionalDropdownActions={[
            critical
              ? null
              : {
                  content: (
                    <Tooltip
                      title={
                        selected.length === 2
                          ? t(
                              'table.compareSelectedTooltip',
                              'Compare the images of the 2 selected Damages side by side'
                            )
                          : t(
                              'table.compareSelectedSelectTooltip',
                              'Select 2 Damages to compare them side by side'
                            )
                      }
                    >
                      <span data-testid="damage-table-additional-action-compare-selected">
                        {t('table.compareSelected', 'Compare Selected')}
                      </span>
                    </Tooltip>
                  ),
                  disabled: selected.length !== 2,
                  onClick: () => {
                    const group1 = selected[0].observationGroups?.find(
                      (g) => g?.isPrimaryObservationGroup
                    );
                    const group2 = selected[1].observationGroups?.find(
                      (g) => g?.isPrimaryObservationGroup
                    );
                    const pictureId1 = group1?.observations?.find(
                      (o) => o?.id === group1.sourceObservationId
                    )?.picture?.id;
                    const pictureId2 = group2?.observations?.find(
                      (o) => o?.id === group2.sourceObservationId
                    )?.picture?.id;

                    if (pictureId1 && pictureId2) {
                      history.push(
                        `/pictures/${pictureId1}?observation=${group1?.sourceObservationId}&picture2=${pictureId2}&observation2=${group2?.sourceObservationId}`,
                        { useModal: true }
                      );
                    }
                  },
                },
            isEditor
              ? {
                  content: (
                    <Tooltip
                      title={
                        selected.length < LINK_DAMAGES.min || selected.length > LINK_DAMAGES.max
                          ? t(
                              'table.linkDamagesSelectRangeTooltip',
                              'Select from {{min}} to {{max}} damages to link.',
                              { min: LINK_DAMAGES.min, max: LINK_DAMAGES.max }
                            )
                          : !selectedDamagesHaveSameAsset
                            ? t(
                                'table.linkDamagesDifferentAssetsTooltip',
                                'Damages are assigned to different assets.'
                              )
                            : t(
                                'table.linkDamagesTooltip',
                                'Link the selected Damages, with their Observations, Tasks and Comments, under one Damage ID'
                              )
                      }
                    >
                      <span data-testid="damage-table-additional-action-link-damages">
                        {t('table.linkDamages', 'Link Damages')}
                      </span>
                    </Tooltip>
                  ),
                  disabled:
                    selected.length < LINK_DAMAGES.min ||
                    selected.length > LINK_DAMAGES.max ||
                    !selectedDamagesHaveSameAsset,
                  onClick: handleMergeSelected,
                }
              : null,
            hasDamagePlotFeatureFlag
              ? {
                  content: (
                    <Tooltip
                      title={
                        plotEnabled
                          ? t(
                              'table.plotDamagesTooltip',
                              "Plot the Damages that match the table's filters by their position on the Blade"
                            )
                          : makeModelsMissingLength.length
                            ? t(
                                'table.plotDamagesMissingLengthTooltip',
                                'Unable to plot because length data is missing for: {{makeModels}}',
                                { makeModels: makeModelsMissingLengthLabel }
                              )
                            : !hasPlotFilters
                              ? t(
                                  'table.plotDamagesFilterTooltip',
                                  'Filter by Blade make/model or specific Blades to plot Damages'
                                )
                              : t(
                                  'table.plotDamagesLengthSpreadTooltip',
                                  'Filtered Blades must have lengths within {{spread}}m of each other to plot Damages together',
                                  { spread: MAX_PLOT_BLADE_LENGTH_SPREAD_M }
                                )
                      }
                    >
                      <span data-testid="damage-table-additional-action-plot-damages">
                        {t('table.plotDamages', 'Plot Damages')}
                      </span>
                    </Tooltip>
                  ),
                  disabled: !plotEnabled,
                  onClick: () => setShowPlot(true),
                }
              : null,
            canAccess([DAMAGE_CONFIRM])
              ? {
                  content: (
                    <Tooltip
                      title={
                        !selected.length
                          ? t('table.confirmDamagesSelectTooltip', 'Select Damages to confirm them')
                          : !getUnconfirmedDamages(selected).length
                            ? t(
                                'table.confirmDamagesAllConfirmedTooltip',
                                'The selected Damages are already confirmed'
                              )
                            : t(
                                'table.confirmDamagesTooltip',
                                'Mark the selected Damages as confirmed'
                              )
                      }
                    >
                      <span data-testid="damage-table-additional-action-confirm-damages">
                        {t('table.confirmDamages', 'Confirm Damages')}
                      </span>
                    </Tooltip>
                  ),
                  disabled: confirming || !getUnconfirmedDamages(selected).length,
                  onClick: handleConfirmSelected,
                }
              : null,
            canAccess([TASK_CREATE])
              ? {
                  content: (
                    <Tooltip
                      title={
                        selected.length < CREATE_TASKS_DAMAGE_LIMITS.min ||
                        selected.length > CREATE_TASKS_DAMAGE_LIMITS.max
                          ? t(
                              'table.createTasksSelectRangeTooltip',
                              'Select from {{min}} to {{max}} damages to create tasks for.',
                              {
                                min: CREATE_TASKS_DAMAGE_LIMITS.min,
                                max: CREATE_TASKS_DAMAGE_LIMITS.max,
                              }
                            )
                          : t('table.createTasksTooltip', 'Create Tasks for the selected Damages')
                      }
                    >
                      <span data-testid="damage-table-additional-action-create-tasks">
                        {t('table.createTasks', { count: selected.length })}
                      </span>
                    </Tooltip>
                  ),
                  disabled:
                    selected.length < CREATE_TASKS_DAMAGE_LIMITS.min ||
                    selected.length > CREATE_TASKS_DAMAGE_LIMITS.max,
                  onClick: () => createTasksForSelectedDamages(selected),
                }
              : null,
            !hiddenActions.includes('idSearch')
              ? {
                  content: (
                    <Tooltip
                      title={t(
                        'table.idSearchTooltip',
                        'Find a Damage by its Damage, Observation, or Legacy Damage ID or UUID'
                      )}
                    >
                      <span data-testid="damage-table-additional-action-id-search">
                        {t('table.idSearch', 'ID Search')}
                      </span>
                    </Tooltip>
                  ),
                  onClick: () => setDamageSearchOpen(true),
                }
              : null,

            !hiddenActions.includes('uploadDamageData') && hasDamageIngestionFeatureFlag
              ? {
                  content: (
                    <Tooltip
                      title={t(
                        'table.uploadDamageDataTooltip',
                        'Upload a file of Damages for one or more Inspections'
                      )}
                    >
                      <span data-testid="damage-table-additional-action-upload-damage-data">
                        {t('table.uploadDamageData', 'Upload Damage Data')}
                      </span>
                    </Tooltip>
                  ),
                  onClick: () => setMultiInspectionDamageIngestionModalOpen(true),
                }
              : null,
          ].filter(Boolean)}
          scrollHeight={scrollHeight}
        />
      </div>
      <CreateTasksFromDamagesModal
        damages={selected}
        open={createTasksFromDamagesModalOpen}
        setOpen={setCreateTasksFromDamagesModalOpen}
      />
      <MergeDamagesModal
        mergeConfirmationState={mergeConfirmationState}
        refetchQueries={mutationRefetchQueries}
        onClose={() => {
          setMergeConfirmationState({ isOpen: false });
        }}
        onSuccess={(damage) => {
          // find selected rows that are no longer in the table
          // (i.e. the ones that were merged into the remaining damage)
          const selectedRows = selected.filter(
            ({ id }) => !mergeConfirmationState.sourceDamageIds?.includes(id)
          );

          // remove the now-merged rows from the table selection
          rowSelection.onChange(
            selectedRows.map(({ id }) => id),
            selectedRows
          );

          // set merged damage to update
          setMergedDamageId(damage.id);

          // for emphasis, deselect the remaining row after a delay
          setTimeout(() => {
            onClearSelection();
          }, 2500);
        }}
      />
      <DamageSearch isOpen={damageSearchOpen} onClose={() => setDamageSearchOpen(false)} />
      <MultiInspectionDamageIngestion
        isOpen={multiInspectionDamageIngestionModalOpen}
        setModalOpen={setMultiInspectionDamageIngestionModalOpen}
      />
    </>
  );
};

export const DamageTable2: React.FunctionComponent<DamageTable2Props> = (props) => {
  const { defaultActiveTabKey = 'ALL', critical = false, siteIds, initialFilters } = props;
  const initialPresetFilter = getInitialPresetFilter({
    critical,
    siteIds,
    defaultActiveTabKey,
    initialFilters,
  });

  return (
    <DataTableContextProvider
      {...omit(props, ['controls'])}
      initialPresetFilter={initialPresetFilter}
    >
      <BaseDamageTable2 {...props} initialPresetFilter={initialPresetFilter} />
    </DataTableContextProvider>
  );
};
